import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createContext } from "use-context-selector";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import type { Dashboard, DashboardsApi } from "@/shell-handle/dashboards";

export interface DashboardsContextValue {
  dashboards: Dashboard[] | undefined;
  loading: boolean;
}

export const DashboardsContext = createContext<DashboardsContextValue>({
  dashboards: [],
  loading: false,
});

export function DashboardsContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const host = useShellHost();
  const handle = host.handle as typeof host.handle & {
    dashboards?: DashboardsApi;
  };

  const [dashboards, setDashboards] = useState<Dashboard[]>(
    handle.dashboards?.list.get() ?? [],
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(
    handle.sites.selectedSiteId.get(),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!handle.dashboards) return;
    return handle.dashboards.list.subscribe(() =>
      setDashboards(handle.dashboards!.list.get()),
    );
  }, [handle]);

  useEffect(
    () =>
      handle.sites.selectedSiteId.subscribe(() =>
        setSelectedSiteId(handle.sites.selectedSiteId.get()),
      ),
    [handle],
  );

  useEffect(() => {
    if (!handle.dashboards || !selectedSiteId) return;
    setLoading(true);
    void handle.dashboards
      .refresh(Number(selectedSiteId))
      .finally(() => setLoading(false));
  }, [handle, selectedSiteId]);

  const value = useMemo(() => ({ dashboards, loading }), [dashboards, loading]);

  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  );
}
